import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CornerDownLeft, Sparkles, X } from "lucide-react";
import { api } from "../lib/api";
import { IMAGES } from "../lib/tokens";
import { WALL_RECT } from "../lib/beats";
import { useWorld } from "../state/World";
import MelzFigure from "../components/MelzFigure";

const DUMP = [
  "Chapter nine still has no ending",
  "Rewrite the opening page — it is still throat-clearing",
  "Find a copy editor I can actually afford",
  "The cover needs a real designer, not me in Canva",
  "Decide: query agents or go straight to KDP",
  "Work out ISBN and the print formats",
  "340 subscribers and I have never written to them",
  "Ask Marion whether she would blurb it",
  "Two hours every morning before the house wakes up",
  "How much can I spend on this without a fight",
];

// MelzAI is listening, not working. One nudge, never a lecture.
const NUDGES = [
  "I am not going to sort anything yet. Just put it down.",
  "More. You are still being tidy about it.",
  "Now the one you have not said out loud.",
  "Keep going. None of this is going upstairs.",
  "That is enough to sort — but say more if it is still coming.",
];

const nudgeFor = (n, held) => {
  if (n === 0 && held > 0) return "Blank wall. I have the rest. Go again.";
  return n === 0 ? NUDGES[0] : n < 3 ? NUDGES[1] : n < 6 ? NUDGES[2] : n < 10 ? NUDGES[3] : NUDGES[4];
};

// Post-it paper, not chalk. Anthony's cold test caught three separate faults on
// this wall: hard to read, words jumping on every add, and a jumble once it
// fills. Paper answers all three — a card draws a boundary around one thought,
// an opaque ground gives the ink something to sit on, and a size derived from
// the Shard's OWN text means adding a Shard can never resize the ones already up.
const PAPERS = [
  { bg: "#f1e6c6", ink: "#221f17" }, // cream
  { bg: "#e6dabc", ink: "#221f18" }, // manila
  { bg: "#e8d9d1", ink: "#241d1a" }, // pale rose
  { bg: "#dce2ce", ink: "#1d2217" }, // pale sage
  { bg: "#ecdfb8", ink: "#231f14" }, // straw
];
const hash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

export default function CoatCloset() {
  const { mark, live } = useWorld();
  const nav = useNavigate();
  const [greeting, setGreeting] = useState(null);
  const [aim, setAim] = useState(null);
  const [carriedSlice, setCarriedSlice] = useState(null);
  const [shards, setShards] = useState([]);
  const [held, setHeld] = useState(0);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const wall = useRef(null);

  useEffect(() => {
    mark("stageward", "/stageward");
    api.post("/seam/open-stageward", {}).catch(() => {});
    api.get("/melzai/stageward-greeting").then(({ data }) => {
      setGreeting(data.greeting);
      setAim(data.carried_aim);
      setCarriedSlice(data.carried_slice);
    });
    // Walk in and the wall is clean. MelzAI scraped it and is holding what was
    // on it — nothing is lost, it is just not clutter any more.
    api
      .post("/stageward/wall", {})
      .then(({ data }) => {
        setHeld(data.held || 0);
        setShards([]);
      })
      .catch(() => {});
  }, [mark]);

  useEffect(() => {
    // Newest at the top, so scroll back UP to it. The wall used to run to the
    // bottom, which meant the thought you had just put up appeared furthest
    // from the box you typed it into.
    wall.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [shards.length]);

  const push = async (texts) => {
    setBusy(true);
    try {
      const { data } = await api.post("/stageward/shards", { texts });
      setShards((s) => [...s, ...data]);
      setHeld((h) => h + data.length);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The wall would not take it.");
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const drop = async (id) => {
    setShards((s) => s.filter((x) => x.id !== id));
    setHeld((h) => Math.max(0, h - 1));
    api.delete(`/stageward/shards/${id}`).catch(() => {});
  };

  const n = shards.length;
  const enough = held + n >= 3;
  const marks = useMemo(
    () =>
      shards
        .map((s) => {
          const h = hash(s.id || s.text);
          const t = s.text || "";
          return {
            ...s,
            // gentle tilt only — enough to look pinned by hand, not enough for
            // neighbouring cards to overlap and re-create the jumble
            tilt: ((h % 500) / 500 - 0.5) * 3.2,
            paper: PAPERS[h % PAPERS.length],
            // size comes from THIS Shard's length, never from how many there are
            size: t.length <= 26 ? "text-[19px]" : t.length <= 64 ? "text-[16px]" : "text-[14px]",
          };
        })
        // Newest first. Display order only — the Sequence the Table works from
        // comes from the server and is untouched by this.
        .reverse(),
    [shards]
  );

  return (
    <div className="relative h-screen overflow-hidden bg-[#0b100c]" data-testid="graffiti-wall">
      {/* The founder's plate. The wall is genuinely blank — every word here is live. */}
      <img
        src={IMAGES.swGraffitiWall}
        alt=""
        data-testid="wall-plate"
        className="absolute inset-0 h-full w-full object-cover"
      />

      <MelzFigure mode="Clarify" live={live} align="left" height="60%" width="12%" />

      {/* MelzAI's one line, low and left, out of the working area. Lifted from
          17% to 27% when the bench below grew a readability strip — the line was
          landing on top of the Shard count. */}
      <div className="absolute bottom-[27%] left-[17%] z-20 hidden w-[34%] md:block">
        <AnimatePresence mode="wait">
          <motion.p
            key={nudgeFor(n, held)}
            className="serif text-[15px] italic leading-snug text-[#e6c98a]/65"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="melz-nudge"
          >
            {nudgeFor(n, held)}
          </motion.p>
        </AnimatePresence>
      </div>

      <button
        type="button"
        data-testid="back-to-atlas-stageward"
        onClick={() => nav("/")}
        className="tracked absolute left-6 top-6 z-30 flex items-center gap-2 text-[9px] text-white/45 transition-colors duration-200 hover:text-[var(--stage-gold)]"
      >
        <ArrowLeft size={12} /> The Atlas
      </button>

      {/* What walked through the Seam, as a brass line under the wall title. */}
      {(carriedSlice || aim) && (
        <motion.div
          className="absolute left-0 right-0 top-[19.5%] z-20 px-6 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
        >
          <div className="tracked text-[8px] text-[var(--stage-gold)]/50">
            carried across the Seam
          </div>
          <div
            className="serif mt-1 text-[15px] italic text-[#e6c98a]/85"
            data-testid="carried-aim"
          >
            {carriedSlice || aim}
          </div>
        </motion.div>
      )}

      {/* ---------------- the wall itself ---------------- */}
      <div
        ref={wall}
        className="absolute z-20 overflow-y-auto"
        style={{
          left: `${WALL_RECT.left + 5}%`,
          right: `${100 - WALL_RECT.right + 2}%`,
          top: `${WALL_RECT.top + 4}%`,
          bottom: `${100 - WALL_RECT.bottom}%`,
        }}
        data-testid="shard-list"
      >
        {n === 0 && (
          <motion.p
            className="serif mx-auto max-w-lg text-center text-[17px] italic leading-relaxed text-[#cfd8bd]/45"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.7 }}
            data-testid="stageward-greeting"
          >
            {held > 0
              ? `I scraped the wall while you were away. Those ${held} are safe with me — they are going to the Table, not back up here. Blank wall. What is on top now?`
              : greeting || "…"}
          </motion.p>
        )}

        {/* A pinboard, not a masonry. CSS multi-column refuses to balance
            inside a scrolling container — it just extends the first column
            forever, which left eleven notes in one narrow strip and the rest of
            the wall empty. A grid fills the wall, and the ragged bottoms read as
            notes put up by hand rather than as a layout.
            Four columns so the cards land near 3x5 index-card proportions
            instead of wide strips. */}
        <div className="grid grid-cols-1 items-start gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <AnimatePresence>
            {marks.map((s) => (
              <motion.div
                key={s.id}
                className="group relative"
                initial={{ opacity: 0, y: -14, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
                style={{ rotate: `${s.tilt}deg` }}
                data-testid="shard-item"
              >
                <div
                  className="relative flex min-h-[190px] flex-col rounded-[2px] px-4 pb-4 pt-6"
                  style={{
                    background: s.paper.bg,
                    boxShadow:
                      "0 10px 22px rgba(0,0,0,0.55), 0 2px 4px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.35)",
                  }}
                >
                  {/* a strip of tape, so it reads as put up by hand */}
                  <span
                    className="absolute left-1/2 top-0 h-4 w-12 -translate-x-1/2 -translate-y-1/2 rounded-[1px]"
                    style={{
                      background: "rgba(236,228,205,0.5)",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
                    }}
                  />
                  <span
                    className={`scrawl block leading-[1.25] ${s.size}`}
                    style={{ color: s.paper.ink }}
                  >
                    {s.text}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => drop(s.id)}
                  className="absolute -right-2 -top-2 rounded-full bg-[#0b100c] p-1 text-white/40 opacity-0 transition-opacity duration-200 hover:text-[var(--stage-ember)] group-hover:opacity-100"
                  aria-label="take it down"
                  data-testid={`shard-drop-${s.id}`}
                >
                  <X size={11} />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* ---------------- the bench: one input, nothing else ---------------- */}
      <div className="absolute inset-x-0 bottom-0 z-30 px-6 pb-6 md:px-14 md:pb-9">
        <div className="mx-auto max-w-3xl">
          <form
            className="flex items-center gap-3 border-b border-[#e6c98a]/25 pb-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!draft.trim()) return;
              push([draft.trim()]);
              setDraft("");
            }}
          >
            <input
              ref={inputRef}
              autoFocus
              className="scrawl min-w-0 flex-1 bg-transparent text-[24px] text-[#f0e6cf] outline-none placeholder:text-[#cfd8bd]/30"
              data-testid="shard-input"
              placeholder="Say it. One thought. Then Enter."
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <button
              type="submit"
              data-testid="shard-add-button"
              disabled={busy || !draft.trim()}
              className="shrink-0 text-[#e6c98a]/60 transition-colors duration-200 hover:text-[#e6c98a] disabled:opacity-25"
            >
              <CornerDownLeft size={16} />
            </button>
          </form>

          {/* Readability: this strip was 10px mono at 25-30% white over a dark
              photographic plate — Anthony could not read it, and neither could
              the founder. Sizes up, opacity up, and the whole strip sits on a
              soft ground so the type has something behind it. Nothing here is
              decorative; it tells you what you have and where the door is. */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-white/10 bg-black/45 px-4 py-3 backdrop-blur-sm">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <span className="mono text-[12px] text-white/70">
                <span data-testid="shard-count" className="text-[15px] font-bold text-[#f0d79b]">
                  {n}
                </span>{" "}
                on the wall
              </span>
              {held > n && (
                <span className="mono text-[12px] text-white/60" data-testid="melz-holding">
                  MelzAI holds{" "}
                  <span className="text-[15px] font-bold text-[var(--arcade-cyan)]">{held}</span>{" "}
                  in all
                </span>
              )}
              <button
                type="button"
                data-testid="brain-dump-button"
                disabled={busy}
                onClick={() => push(DUMP.filter((d) => !shards.some((s) => s.text === d)))}
                className="mono flex items-center gap-1.5 text-[12px] text-white/55 underline decoration-white/20 underline-offset-4 transition-colors duration-200 hover:text-[#f0d79b] hover:decoration-[#f0d79b]/50"
              >
                <Sparkles size={12} /> dump ten for me
              </button>
            </div>

            {/* What's next. One door, and it only opens when there is a mess. */}
            <button
              type="button"
              data-testid="to-the-table-button"
              disabled={!enough || busy}
              onClick={() => nav("/stageward/drawing")}
              className="tracked group flex shrink-0 items-center gap-2 rounded-full border-2 border-[var(--stage-gold)]/60 bg-[var(--stage-gold)]/10 px-6 py-3 text-[11px] font-semibold text-[var(--stage-gold)] transition-colors duration-200 hover:border-[var(--stage-gold)] hover:bg-[var(--stage-gold)]/20 disabled:border-white/15 disabled:bg-transparent disabled:text-white/30"
            >
              {enough ? "Take it to the Drawing Table" : "Three, then the Table"}
              <ArrowRight
                size={13}
                strokeWidth={2.2}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </button>
          </div>

          {greeting && !aim && !carriedSlice && (
            <div className="mt-4" data-testid="seam-nudge">
              <button
                type="button"
                data-testid="nudge-to-playforward"
                onClick={() => nav("/playforward/cove")}
                className="mono rounded-lg border border-[var(--arcade-cyan)]/35 bg-[var(--arcade-cyan)]/10 px-4 py-2.5 text-left text-[12px] leading-relaxed text-[var(--arcade-cyan)] transition-colors duration-200 hover:bg-[var(--arcade-cyan)]/20"
              >
                → You came in the back door. Cut a Slice in PlayForward first (60
                seconds)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
